/* BP-008 — Rental Intelligence tab.

   Layout:
     • Inputs grid  — acquisition, financing, income, expenses
     • Hero KPI     — Monthly Cash Flow + Cap Rate + CoC + DSCR
     • Metrics grid — NOI · EGI · Op Ex · Break-even Occupancy
     • Warnings     — codes + severity
     • Equity 5yr   — table + progress bars
     • AI panel     — narrative + strategy comparison
     • History      — versioned saved analyses
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import RentYieldGuardrail from "../RentYieldGuardrail";
import {
  Loader2, RotateCcw, Save, Sparkles, Download, Home,
} from "lucide-react";
import { CashFlowProjection } from "@/components/v3/PropertyCharts";

const INP = "w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100";

const EMPTY_INPUTS = {
  purchase_price: "", arv: "", repair_budget: "0",
  down_payment: "", loan_amount: "", interest_rate: "", loan_term_years: "",
  monthly_rent: "", vacancy_rate: "",
  property_taxes: "", insurance: "", hoa: "0",
  maintenance: "", property_management: "", utilities: "0", capex: "",
};

export default function RentalTab({ property }) {
  const [inputs, setInputs] = useState({
    ...EMPTY_INPUTS,
    purchase_price: property.asking_price || "",
    arv: property.published_arv || property.estimated_arv || "",
    repair_budget: property.published_repair_estimate?.total || "0",
  });
  const [scenarioName, setScenarioName] = useState("Base Case");
  const [current, setCurrent] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v3/properties/${property.id}/rental`);
      setAnalyses(data.items || []);
      if (data.items?.[0] && !current) {
        setCurrent(data.items[0]);
        setInputs({ ...EMPTY_INPUTS, ...data.items[0].inputs });
        setScenarioName(data.items[0].scenario_name);
      }
    } catch { toast.error("Failed to load Rental Intelligence"); }
    finally { setLoading(false); }
  }, [property.id, current]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setInputs((s) => ({ ...s, [k]: e.target.value }));

  const calc = useCallback(async (save) => {
    if (!inputs.purchase_price || !inputs.monthly_rent) {
      toast.error("Purchase Price and Monthly Rent are required.");
      return;
    }
    setCalculating(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${property.id}/rental/calculate`,
        { inputs, save, scenario_name: scenarioName },
      );
      if (save) {
        setCurrent(data);
        toast.success(`${data.scenario_name} saved`);
        load();
      } else {
        setCurrent({ inputs, outputs: data.outputs, scenario_name: scenarioName });
        toast.success("Calculated");
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Calculation failed"); }
    finally { setCalculating(false); }
  }, [inputs, property.id, scenarioName, load]);

  const runAi = useCallback(async () => {
    if (!current?.id) { toast.error("Save the analysis first."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post(`/v3/rental/${current.id}/ai-review`);
      setCurrent(data);
      toast.success("AI review ready");
    } catch { toast.error("AI review failed"); }
    finally { setAiLoading(false); }
  }, [current]);

  const exportPdf = useCallback(() => {
    if (!current?.id) return;
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/v3/rental/${current.id}/export/pdf`, "_blank");
  }, [current]);

  const outs = useMemo(() => current?.outputs || {}, [current]);

  if (loading) return (
    <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="rental-loading">
      <Loader2 size={14} className="animate-spin"/> Loading…
    </div>
  );

  return (
    <div className="space-y-4" data-testid="tab-panel-rental">
      {/* Rent-Yield Guardrail — market-class selector + red/green light */}
      <RentYieldGuardrail
        purchasePrice={inputs.purchase_price}
        monthlyRent={inputs.monthly_rent}
      />

      {/* Inputs */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 mb-3">
          <Home size={12}/> Rental Inputs
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <F label="Purchase Price *"><input value={inputs.purchase_price} onChange={set("purchase_price")} data-testid="rt-purchase" className={INP} type="number"/></F>
          <F label="Repair Budget"><input value={inputs.repair_budget} onChange={set("repair_budget")} data-testid="rt-repairs" className={INP} type="number"/></F>
          <F label="ARV (optional)"><input value={inputs.arv} onChange={set("arv")} className={INP} type="number" placeholder="—"/></F>
          <F label="Down Payment (blank → 20%)"><input value={inputs.down_payment} onChange={set("down_payment")} className={INP} type="number" placeholder="auto"/></F>
          <F label="Loan Amount (blank → auto)"><input value={inputs.loan_amount} onChange={set("loan_amount")} className={INP} type="number" placeholder="auto"/></F>
          <F label="Interest Rate (0.07 = 7%)"><input value={inputs.interest_rate} onChange={set("interest_rate")} className={INP} type="number" step="0.001" placeholder="0.07"/></F>
          <F label="Loan Term (yrs)"><input value={inputs.loan_term_years} onChange={set("loan_term_years")} className={INP} type="number" placeholder="30"/></F>
          <F label="Monthly Rent *"><input value={inputs.monthly_rent} onChange={set("monthly_rent")} data-testid="rt-rent" className={INP} type="number"/></F>
          <F label="Vacancy Rate (0.05 = 5%)"><input value={inputs.vacancy_rate} onChange={set("vacancy_rate")} className={INP} type="number" step="0.01" placeholder="0.05"/></F>
          <F label="Property Taxes ($/mo)"><input value={inputs.property_taxes} onChange={set("property_taxes")} className={INP} type="number"/></F>
          <F label="Insurance ($/mo)"><input value={inputs.insurance} onChange={set("insurance")} className={INP} type="number"/></F>
          <F label="HOA ($/mo)"><input value={inputs.hoa} onChange={set("hoa")} className={INP} type="number"/></F>
          <F label="Maintenance ($/mo · blank → 8% rent)"><input value={inputs.maintenance} onChange={set("maintenance")} className={INP} type="number" placeholder="auto"/></F>
          <F label="Property Mgmt ($/mo · blank → 10% rent)"><input value={inputs.property_management} onChange={set("property_management")} className={INP} type="number" placeholder="auto"/></F>
          <F label="Utilities ($/mo)"><input value={inputs.utilities} onChange={set("utilities")} className={INP} type="number"/></F>
          <F label="CapEx ($/mo · blank → 5% rent)"><input value={inputs.capex} onChange={set("capex")} className={INP} type="number" placeholder="auto"/></F>
          <F label="Scenario Name"><input value={scenarioName} onChange={(e) => setScenarioName(e.target.value)} data-testid="rt-name" className={INP}/></F>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => calc(false)} disabled={calculating} data-testid="rt-calc"
                  className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
            {calculating ? <Loader2 size={12} className="animate-spin"/> : <RotateCcw size={12}/>}
            Calculate
          </button>
          <button onClick={() => calc(true)} disabled={calculating} data-testid="rt-save"
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-emerald-500 disabled:opacity-50">
            <Save size={12}/> Save
          </button>
          <button onClick={runAi} disabled={aiLoading || !current?.id} data-testid="rt-ai"
                  className="inline-flex items-center gap-1 rounded-md border border-purple-300 bg-purple-50 text-purple-800 text-xs font-semibold px-3 py-1.5 hover:border-purple-500 disabled:opacity-40">
            {aiLoading ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
            AI Review
          </button>
          <button onClick={exportPdf} disabled={!current?.id} data-testid="rt-pdf"
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-1.5 hover:border-slate-400 disabled:opacity-40">
            <Download size={12}/> PDF
          </button>
        </div>
      </div>

      {/* Hero KPI */}
      {current && (
        <>
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="rt-results">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-amber-800/80">Monthly Cash Flow</div>
                <div className="text-4xl font-bold text-slate-900 tabular-nums mt-1" data-testid="rt-monthly-cf">
                  ${fmt(outs.monthly_cash_flow)}
                </div>
                <div className="text-xs text-slate-700 mt-1">
                  Annual <b>${fmt(outs.annual_cash_flow)}</b> · NOI <b>${fmt(outs.noi)}</b> · Debt/mo <b>${fmt(outs.monthly_debt_service)}</b>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-right">
                <KPI label="Cap Rate" value={pct(outs.cap_rate)} testid="rt-cap"/>
                <KPI label="Cash-on-Cash" value={pct(outs.cash_on_cash)} testid="rt-coc"/>
                <KPI label="DSCR" value={ratio(outs.dscr)} testid="rt-dscr"/>
              </div>
            </div>
          </div>

          {/* 12-month cash-flow projection */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 md:p-6" data-testid="rt-projection-chart">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">12-Month Projection</div>
            <div className="text-sm font-semibold text-slate-900 mb-3">
              Cumulative cash flow compounds monthly — sustained positive months build equity fast
            </div>
            <CashFlowProjection monthlyCashFlow={outs.monthly_cash_flow} months={12} />
          </div>

          {/* Secondary metrics */}
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
            <Metric label="Gross Annual Income" value={`$${fmt(outs.gross_annual_income)}`}/>
            <Metric label="Effective Gross Income" value={`$${fmt(outs.effective_gross_income)}`}/>
            <Metric label="Operating Expenses (ann.)" value={`$${fmt(outs.operating_expenses_annual)}`}/>
            <Metric label="Break-even Occupancy" value={pct(outs.break_even_occupancy)}/>
          </div>

          {/* Warnings */}
          {(outs.warnings || []).length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-1" data-testid="rt-warnings">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Warnings</div>
              {outs.warnings.map((w, i) => (
                <div key={i} className="text-[11px] text-slate-800">
                  <span className="text-[9px] px-1 py-0.5 rounded bg-slate-100 text-slate-700 uppercase font-semibold mr-2">{w.severity}</span>
                  {w.message}
                </div>
              ))}
            </div>
          )}

          {/* 5-year equity projection */}
          {(outs.equity_projection || []).length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="rt-projection">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">5-Year Equity Projection</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Year</th>
                      <th className="text-right px-3 py-2 font-medium">Appreciation Equity</th>
                      <th className="text-right px-3 py-2 font-medium">Principal Paid</th>
                      <th className="text-right px-3 py-2 font-medium">Total Equity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outs.equity_projection.map((row) => {
                      const maxTotal = Math.max(...outs.equity_projection.map((r) => parseFloat(r.total_equity) || 0));
                      const shareN = maxTotal > 0 ? (parseFloat(row.total_equity) / maxTotal) * 100 : 0;
                      return (
                        <tr key={row.year} className="border-t border-slate-100"
                            data-testid={`rt-proj-y${row.year}`}>
                          <td className="px-3 py-1.5 text-slate-800">Y{row.year}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">${fmt(row.appreciation_equity)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">${fmt(row.principal_paid)}</td>
                          <td className="px-3 py-1.5 text-right">
                            <div className="tabular-nums font-semibold text-slate-900">${fmt(row.total_equity)}</div>
                            <div className="h-1 mt-0.5 bg-slate-100 rounded overflow-hidden">
                              <div className="h-full bg-amber-400 rounded" style={{ width: `${shareN}%` }}/>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* AI panel */}
          {current.ai_review && (
            <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4 space-y-1" data-testid="rt-ai-block">
              <div className="text-[10px] uppercase tracking-wider text-purple-700">AI Review</div>
              <div className="text-sm text-slate-900">{current.ai_review.summary}</div>
              {current.ai_review.rentAdjustmentAdvice && (
                <div className="text-[11px] text-slate-700"><b>Rent adjustment:</b> {current.ai_review.rentAdjustmentAdvice}</div>
              )}
              {current.ai_review.cashFlowRisks?.length > 0 && (
                <div className="text-[11px] text-slate-700"><b>Cash-flow risks:</b> {current.ai_review.cashFlowRisks.join("; ")}</div>
              )}
              {current.ai_review.expenseReductionOpportunities?.length > 0 && (
                <div className="text-[11px] text-slate-700"><b>Expense reduction:</b> {current.ai_review.expenseReductionOpportunities.join("; ")}</div>
              )}
              {current.ai_review.strategyComparison?.recommended && (
                <div className="text-[11px] text-slate-700">
                  <b>Strategy comparison:</b> Recommended <b>{current.ai_review.strategyComparison.recommended}</b>. {current.ai_review.strategyComparison.reasoning}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* History */}
      {analyses.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="rt-history">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Saved Analyses ({analyses.length})</div>
          <div className="space-y-1">
            {analyses.map((a) => (
              <button key={a.id}
                      onClick={() => { setCurrent(a); setInputs({ ...EMPTY_INPUTS, ...a.inputs }); }}
                      data-testid={`rt-history-${a.id.slice(0,8)}`}
                      className="w-full text-left flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-slate-50">
                <span>v{a.version} · {a.scenario_name}</span>
                <span className="tabular-nums text-slate-600">
                  CF/mo ${fmt(a.outputs?.monthly_cash_flow)} · Cap {pct(a.outputs?.cap_rate)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    {children}
  </label>
);
const Metric = ({ label, value }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3">
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-base font-semibold text-slate-900 tabular-nums mt-0.5">{value}</div>
  </div>
);
const KPI = ({ label, value, testid }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-amber-800/80">{label}</div>
    <div className="text-2xl font-bold text-slate-900 tabular-nums mt-1" data-testid={testid}>{value}</div>
  </div>
);

function fmt(v) {
  if (v == null || v === "") return "—";
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? String(v) : n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function pct(v) {
  if (v == null || v === "") return "—";
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? String(v) : `${(n * 100).toFixed(1)}%`;
}
function ratio(v) {
  if (v == null || v === "") return "—";
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? String(v) : n.toFixed(2);
}
